import {
  FALLBACK_ELEMENT_ID,
  IFRAME_ELEMENT_ID,
  SCRIPT_ATTR,
  SCRIPT_ELEMENT_ID,
  TRIGGER_CLASS,
  WEBCHAT_EMBED_ENV,
  WEBCHAT_EMBED_VERSION,
  WEBCHAT_UI_ORIGIN,
} from './constants';
import {
  WebChatInitError,
  type DiagnosticsData,
  type DiagnosticsErrorCode,
  type DiagnosticsHealth,
  type DiagnosticsIssue,
  type DiagnosticsReport,
  type DiagnosticsState,
  type WebChatErrorDetail,
} from './types';

const LOG_PREFIX = '[st-webchat]';
const MAX_EVENTS = 50;
const GLOBAL_API_KEY = '__ST_WEBCHAT__';
const GLOBAL_SNAPSHOT_KEY = '__ST_WEBCHAT_DIAG__';

export interface StWebChatGlobal {
  diagnose: () => DiagnosticsReport;
  version: string;
  env: string;
}

declare global {
  interface Window {
    [GLOBAL_API_KEY]?: StWebChatGlobal;
    [GLOBAL_SNAPSHOT_KEY]?: DiagnosticsReport;
  }
}

interface RuntimeContext {
  tenantId?: string;
  agentId?: string;
  staticContainerId?: string;
  affiliate?: string;
  campaign?: string;
  autoOpen?: string;
  floatingButton?: string;
  webchatPosition?: string;
  floatingButtonShape?: string;
  iframeSrc?: string;
  iframeOrigin?: string;
  embedScriptSrc?: string;
}

interface RuntimeState {
  state: DiagnosticsState;
  readyAt?: string;
  timedOutAt?: string;
  iframeLoadAt?: string;
  lastError?: WebChatErrorDetail;
  events: Array<{ t: string; type: string; detail?: string }>;
  context: RuntimeContext;
  installed: boolean;
  apiPresent: boolean;
}

const runtime: RuntimeState = {
  state: 'not-inited',
  events: [],
  context: {},
  installed: false,
  apiPresent: false,
};

function nowIso(): string {
  return new Date().toISOString();
}

function peekScriptElement(): HTMLScriptElement | null {
  return document.getElementById(SCRIPT_ELEMENT_ID) as HTMLScriptElement | null;
}

/** Best-effort script peek — works even when required attrs are missing. */
export function peekScriptInstallHints(): RuntimeContext {
  const script = peekScriptElement();
  if (!script) return {};

  const {
    tenantId,
    agentId,
    staticContainerId,
    affiliate,
    campaign,
    autoOpen,
    floatingButton,
    autoOpenButton,
    webchatPosition,
    floatingButtonShape,
    autoOpenButtonShape,
  } = script.dataset;
  return {
    embedScriptSrc: script.src || undefined,
    tenantId,
    agentId,
    staticContainerId,
    affiliate,
    campaign,
    autoOpen,
    // data-auto-open-button/-shape are the pre-SETO-7735 names for these two attributes (see
    // SCRIPT_ATTR.legacyFloatingButton/legacyFloatingButtonShape in constants.ts) -- kept as
    // synonyms so existing tenant installs don't silently lose the button. The new name wins when
    // both are present.
    floatingButton: floatingButton ?? autoOpenButton,
    webchatPosition,
    floatingButtonShape: floatingButtonShape ?? autoOpenButtonShape,
  };
}

export function isDebugEnabled(): boolean {
  try {
    if (typeof location !== 'undefined') {
      const params = new URLSearchParams(location.search);
      if (params.get('st-webchat-debug') === '1') return true;
    }
    if (typeof localStorage !== 'undefined' && localStorage.getItem('stWebchatDebug') === '1') {
      return true;
    }
    if (peekScriptElement()?.getAttribute('data-debug') === '1') return true;
  } catch {
    // localStorage / location can throw in sandboxed contexts — treat as off.
  }
  return false;
}

export function debugLog(message: string, detail?: unknown): void {
  if (!isDebugEnabled()) return;
  if (detail === undefined) {
    console.info(`${LOG_PREFIX} ${message}`);
    return;
  }
  console.info(`${LOG_PREFIX} ${message}`, detail);
}

export function recordDiagEvent(type: string, detail?: string): void {
  runtime.events.push({ t: nowIso(), type, detail });
  if (runtime.events.length > MAX_EVENTS) {
    runtime.events.splice(0, runtime.events.length - MAX_EVENTS);
  }
  publishSnapshot();
}

export function setDiagnosticsContext(partial: RuntimeContext): void {
  runtime.context = { ...runtime.context, ...partial };
  publishSnapshot();
}

export function setDiagnosticsState(state: DiagnosticsState): void {
  if (runtime.state === state) {
    syncDomDiagAttrs();
    return;
  }
  runtime.state = state;
  if (state === 'ready' && !runtime.readyAt) runtime.readyAt = nowIso();
  if (state === 'timed-out' && !runtime.timedOutAt) runtime.timedOutAt = nowIso();
  recordDiagEvent('state', state);
  syncDomDiagAttrs();
  debugLog(`state → ${state}`);
}

/** Clear a prior error after a late recovery (e.g. ready after timeout). */
export function clearDiagnosticsError(): void {
  runtime.lastError = undefined;
  syncDomDiagAttrs();
  publishSnapshot();
}

export function setDiagnosticsError(code: DiagnosticsErrorCode, message: string): void {
  runtime.lastError = { code, message };
  recordDiagEvent('error', code);
  syncDomDiagAttrs();
  console.warn(`${LOG_PREFIX} ${code}: ${message}`);
  publishSnapshot();
}

export function noteIframeLoaded(): void {
  runtime.iframeLoadAt = nowIso();
  recordDiagEvent('iframe-load');
  debugLog('iframe load event');
}

function isFallbackVisible(fallback: HTMLElement | null): boolean {
  if (!fallback) return false;
  // showFallback sets display:flex; hideFallback resets to closed style (no display:flex).
  return fallback.style.display === 'flex';
}

function collectChecks(fallbackVisible: boolean): DiagnosticsData['checks'] {
  const script = peekScriptElement();
  return {
    scriptTagFound: Boolean(script),
    webChatApiPresent: typeof window._webChat?.open === 'function' || runtime.apiPresent,
    iframeInDom: Boolean(document.getElementById(IFRAME_ELEMENT_ID)),
    fallbackInDom: Boolean(document.getElementById(FALLBACK_ELEMENT_ID)),
    fallbackVisible,
    triggerCount: document.getElementsByClassName(TRIGGER_CLASS).length,
  };
}

type SummarizeInput = {
  state: DiagnosticsState;
  tenantId?: string;
  agentId?: string;
  iframeLoadAt?: string;
  lastError?: WebChatErrorDetail;
  checks: DiagnosticsData['checks'];
};

function issue(
  code: string,
  severity: DiagnosticsIssue['severity'],
  message: string,
): DiagnosticsIssue {
  return { code, severity, message };
}

function dedupeIssuesByCode(issues: DiagnosticsIssue[]): DiagnosticsIssue[] {
  const seen = new Set<string>();
  return issues.filter((item) => {
    if (seen.has(item.code)) return false;
    seen.add(item.code);
    return true;
  });
}

/** DOM/API gaps that only matter after init has started (and no hard lastError yet). */
function collectPostInitDomIssues(
  checks: DiagnosticsData['checks'],
  lastError: SummarizeInput['lastError'],
): DiagnosticsIssue[] {
  const issues: DiagnosticsIssue[] = [];
  if (!checks.webChatApiPresent && !lastError) {
    issues.push(
      issue('WEB_CHAT_API_MISSING', 'error', 'window._webChat is missing — init likely failed'),
    );
  }
  if (!checks.iframeInDom && !lastError) {
    issues.push(issue('IFRAME_MISSING', 'error', `Iframe #${IFRAME_ELEMENT_ID} is not in the DOM`));
  }
  return issues;
}

function fallbackVisibleMessage(lastError: WebChatErrorDetail | undefined): string {
  return lastError?.code === 'CHAT_DISABLED'
    ? 'Fallback UI is showing — chat is disabled for this tenant'
    : 'Fallback UI is showing — chat iframe did not become ready in time';
}

function collectInstallIssues(input: SummarizeInput): DiagnosticsIssue[] {
  const { state, checks, lastError } = input;
  const issues: DiagnosticsIssue[] = [];
  const iframeNeverLoaded = state === 'timed-out' && !input.iframeLoadAt;

  // Script tag is required for the usual UMD snippet install. ESM/programmatic init() can
  // supply tenant/agent without one — only treat a missing tag as fatal when IDs are also absent.
  if (!checks.scriptTagFound && (!input.tenantId || !input.agentId)) {
    issues.push(
      issue(
        'SCRIPT_TAG_MISSING',
        'error',
        `Script tag #${SCRIPT_ELEMENT_ID} not found on the page`,
      ),
    );
  }
  if (!input.tenantId) {
    issues.push(
      issue(
        'TENANT_ID_MISSING',
        'error',
        `Missing required ${SCRIPT_ATTR.tenantId} (script attribute or init options)`,
      ),
    );
  }
  if (!input.agentId) {
    issues.push(
      issue(
        'AGENT_ID_MISSING',
        'error',
        `Missing required ${SCRIPT_ATTR.agentId} (script attribute or init options)`,
      ),
    );
  }
  if (iframeNeverLoaded) {
    issues.push(
      issue(
        'IFRAME_NEVER_LOADED',
        'error',
        'Iframe never fired load — check CSP frame-src/child-src or the baked UI origin',
      ),
    );
  }
  // Prefer IFRAME_NEVER_LOADED over the generic READY_TIMEOUT when the iframe never loaded.
  if (lastError && !(iframeNeverLoaded && lastError.code === 'READY_TIMEOUT')) {
    issues.push(issue(lastError.code, 'error', lastError.message));
  } else if (state === 'timed-out' && !iframeNeverLoaded && !lastError) {
    issues.push(
      issue(
        'READY_TIMEOUT',
        'error',
        'iframe loaded but webchat:ready never arrived (origin allowlist, referrer, or UI error)',
      ),
    );
  }

  if (state !== 'not-inited') {
    issues.push(...collectPostInitDomIssues(checks, lastError));
  }
  if (checks.fallbackVisible) {
    issues.push(issue('FALLBACK_VISIBLE', 'error', fallbackVisibleMessage(lastError)));
  }
  if (checks.triggerCount === 0 && checks.webChatApiPresent) {
    issues.push(
      issue(
        'NO_OPEN_TRIGGERS',
        'warning',
        `No .${TRIGGER_CLASS} elements found — chat only opens via window._webChat.open()`,
      ),
    );
  }

  return issues;
}

function resolveHealth(state: DiagnosticsState, issues: DiagnosticsIssue[]): DiagnosticsHealth {
  if (issues.some((item) => item.severity === 'error') || state === 'timed-out') {
    return 'error';
  }
  if (state === 'ready' || state === 'open') {
    return 'ok';
  }
  // not-inited / waiting-ready without a hard error yet
  return 'loading';
}

function resolveSummary(
  health: DiagnosticsHealth,
  state: DiagnosticsState,
  issues: DiagnosticsIssue[],
): string {
  if (health === 'ok') {
    return state === 'open'
      ? 'Install looks good — widget is open.'
      : 'Install looks good — widget is ready.';
  }
  if (health === 'loading') {
    return state === 'waiting-ready'
      ? 'Loader started — waiting for webchat:ready from the iframe.'
      : 'Loader present — init has not completed yet.';
  }
  const firstError = issues.find((item) => item.severity === 'error');
  return firstError
    ? `${firstError.code}: ${firstError.message}`
    : 'Install has errors — see issues[].';
}

/**
 * Derive health / summary / issues from raw report fields. Exported for unit tests.
 */
export function summarizeDiagnostics(
  input: SummarizeInput,
): Pick<DiagnosticsReport, 'health' | 'summary' | 'issues'> {
  const issues = dedupeIssuesByCode(collectInstallIssues(input));
  const health = resolveHealth(input.state, issues);
  return { health, summary: resolveSummary(health, input.state, issues), issues };
}

export function buildDiagnosticsReport(): DiagnosticsReport {
  const fallback = document.getElementById(FALLBACK_ELEMENT_ID);
  const hints = peekScriptInstallHints();
  const ctx = { ...hints, ...runtime.context };

  let parentOrigin: string | undefined;
  let parentPath: string | undefined;
  try {
    parentOrigin = location.origin;
    parentPath = location.pathname;
  } catch {
    // ignore
  }

  const checks = collectChecks(isFallbackVisible(fallback));
  const data: DiagnosticsData = {
    version: WEBCHAT_EMBED_VERSION,
    env: WEBCHAT_EMBED_ENV,
    uiOrigin: WEBCHAT_UI_ORIGIN,
    generatedAt: nowIso(),
    embedScriptSrc: ctx.embedScriptSrc,
    tenantId: ctx.tenantId,
    agentId: ctx.agentId,
    staticContainerId: ctx.staticContainerId,
    // review: AMattRiley -- captured into RuntimeContext (peekScriptInstallHints/
    // setDiagnosticsContext) but never surfaced here, so a tenant debugging a config that "doesn't
    // render as expected" couldn't see it in diagnose() at all.
    webchatPosition: ctx.webchatPosition,
    floatingButtonShape: ctx.floatingButtonShape,
    parentOrigin,
    parentPath,
    iframeSrc: ctx.iframeSrc,
    iframeOrigin: ctx.iframeOrigin,
    state: runtime.state,
    readyAt: runtime.readyAt,
    timedOutAt: runtime.timedOutAt,
    iframeLoadAt: runtime.iframeLoadAt,
    lastError: runtime.lastError,
    events: [...runtime.events],
    checks,
  };

  return {
    ...summarizeDiagnostics({
      state: data.state,
      tenantId: data.tenantId,
      agentId: data.agentId,
      iframeLoadAt: data.iframeLoadAt,
      lastError: data.lastError,
      checks: data.checks,
    }),
    data,
  };
}

function publishSnapshot(): void {
  if (typeof window === 'undefined') return;
  window[GLOBAL_SNAPSHOT_KEY] = buildDiagnosticsReport();
}

function writeStateErrorAttrs(el: Element | null): void {
  if (!el) return;
  el.setAttribute('data-st-state', runtime.state);
  if (runtime.lastError) {
    el.setAttribute('data-st-error-code', runtime.lastError.code);
  } else {
    el.removeAttribute('data-st-error-code');
  }
}

function syncDomDiagAttrs(): void {
  writeStateErrorAttrs(document.getElementById(IFRAME_ELEMENT_ID));
  writeStateErrorAttrs(document.getElementById(FALLBACK_ELEMENT_ID));

  const script = peekScriptElement();
  if (script) {
    script.setAttribute('data-st-webchat-version', WEBCHAT_EMBED_VERSION);
    script.setAttribute('data-st-webchat-env', WEBCHAT_EMBED_ENV);
  }
}

/**
 * Build a diagnostics report, log it, and refresh the global snapshot.
 * Safe to call when init failed or never ran. Console-only — no toast/clipboard.
 * Public trigger: `window.__ST_WEBCHAT__.diagnose()`.
 */
export function diagnose(): DiagnosticsReport {
  const report = buildDiagnosticsReport();
  window[GLOBAL_SNAPSHOT_KEY] = report;
  // Lead with the one-liner so the console is skimmable before expanding the object.
  console.info(`${LOG_PREFIX} ${report.summary}`, report);
  return report;
}

/**
 * Install `window.__ST_WEBCHAT__`. Idempotent; safe before/after init failure.
 */
export function installDiagnostics(): void {
  if (typeof window === 'undefined') return;

  // Always refresh context from the script tag when present.
  setDiagnosticsContext(peekScriptInstallHints());

  window[GLOBAL_API_KEY] = {
    diagnose,
    version: WEBCHAT_EMBED_VERSION,
    env: WEBCHAT_EMBED_ENV,
  };
  syncDomDiagAttrs();

  if (!runtime.installed) {
    runtime.installed = true;
    recordDiagEvent('diagnostics-installed');
    debugLog('diagnostics installed', {
      version: WEBCHAT_EMBED_VERSION,
      env: WEBCHAT_EMBED_ENV,
      uiOrigin: WEBCHAT_UI_ORIGIN,
    });
  }
}

/** Map init/bootstrap throws to stable error codes for the report. */
export function classifyInitError(error: unknown): DiagnosticsErrorCode {
  if (error instanceof WebChatInitError) return error.code;
  return 'INIT_FAILED';
}

/** Record a bootstrap failure unless one is already stored (UMD catch after init). */
export function recordInitFailure(error: unknown): void {
  if (runtime.lastError) return;
  const message = error instanceof Error ? error.message : String(error);
  setDiagnosticsError(classifyInitError(error), message);
}

/** Mark that init() returned an API — ESM consumers never assign window._webChat. */
export function markDiagnosticsApiPresent(): void {
  runtime.apiPresent = true;
  publishSnapshot();
}

/**
 * Record a ready-timeout error. Callers set the lifecycle state separately (init.ts's
 * enterFallback) -- this only ever sets lastError, so a no-op call never happens.
 */
export function noteReadyTimeout(iframeLoaded: boolean): WebChatErrorDetail {
  const detail: WebChatErrorDetail = iframeLoaded
    ? {
        code: 'READY_TIMEOUT',
        message:
          'iframe loaded but webchat:ready never arrived (origin allowlist, referrer, or UI error)',
      }
    : {
        code: 'READY_TIMEOUT',
        message:
          'iframe did not load and webchat:ready never arrived (CSP frame-src, network, or wrong UI origin)',
      };
  setDiagnosticsError(detail.code, detail.message);
  return detail;
}

/**
 * Record an explicit webchat:disabled from the child (SETO-7715) -- a real, immediate signal
 * rather than a guess made after the deadline elapsed with no response at all. Same shape as
 * noteReadyTimeout: only sets lastError, state is init.ts's responsibility.
 */
export function noteChatDisabled(): WebChatErrorDetail {
  const detail: WebChatErrorDetail = {
    code: 'CHAT_DISABLED',
    message: 'chat is disabled for this tenant (feature gate or tenant toggle off)',
  };
  setDiagnosticsError(detail.code, detail.message);
  return detail;
}

/** Test-only reset between cases. */
export function resetDiagnosticsForTests(): void {
  runtime.state = 'not-inited';
  runtime.readyAt = undefined;
  runtime.timedOutAt = undefined;
  runtime.iframeLoadAt = undefined;
  runtime.lastError = undefined;
  runtime.events = [];
  runtime.context = {};
  runtime.installed = false;
  runtime.apiPresent = false;
  if (typeof window !== 'undefined') {
    delete window[GLOBAL_API_KEY];
    delete window[GLOBAL_SNAPSHOT_KEY];
  }
}
